import { useState, useEffect } from 'react';
import { knowledgeAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, AlertTriangle } from 'lucide-react';

export default function NewArticleDialog({ open, onOpenChange, onCreated, article }) {
  const isEdit = Boolean(article?.id);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: '',
    warning: '',
    is_published: true,
  });

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title || '',
        content: article.content || '',
        category: article.category || '',
        tags: (article.tags || []).join(', '),
        warning: article.warning || '',
        is_published: article.is_published ?? true,
      });
    } else if (open) {
      setFormData({ title: '', content: '', category: '', tags: '', warning: '', is_published: true });
    }
  }, [article, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map((t) => t.trim()).filter(Boolean),
        warning: formData.warning?.trim() || null,
      };
      if (isEdit) {
        await knowledgeAPI.update(article.id, payload);
      } else {
        await knowledgeAPI.create(payload);
      }
      onOpenChange(false);
      setFormData({ title: '', content: '', category: '', tags: '', warning: '', is_published: true });
      onCreated?.();
    } catch (err) {
      console.error('Failed to save article:', err);
      alert('Failed to save article');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-800">{isEdit ? 'Edit Knowledge Article' : 'Create Knowledge Article'}</DialogTitle>
          <DialogDescription className="text-slate-500">
            Write a help article for customers and agents
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="article-title" className="text-slate-700">Title <span className="text-red-500">*</span></Label>
            <Input
              id="article-title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Article title"
              className="bg-white border-slate-200 h-11"
              required
              data-testid="article-title-input"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="article-category" className="text-slate-700">Category <span className="text-red-500">*</span></Label>
              <Input
                id="article-category"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g. Getting Started"
                className="bg-white border-slate-200 h-11"
                required
                data-testid="article-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="article-tags" className="text-slate-700">Tags (comma-separated)</Label>
              <Input
                id="article-tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="setup, account, billing"
                className="bg-white border-slate-200 h-11"
                data-testid="article-tags-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-warning" className="text-slate-700 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Warning banner <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <Textarea
              id="article-warning"
              value={formData.warning}
              onChange={(e) => setFormData({ ...formData, warning: e.target.value })}
              placeholder="e.g. This procedure requires admin privileges. Do not run during business hours."
              className="bg-amber-50/50 border-amber-200 min-h-[70px]"
              data-testid="article-warning-input"
            />
            <p className="text-xs text-slate-500">Shown as a yellow banner at the top of the article. Leave empty to hide.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="article-content" className="text-slate-700">Content <span className="text-red-500">*</span></Label>
            <Textarea
              id="article-content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your article content here..."
              className="bg-white border-slate-200 min-h-[250px]"
              required
              data-testid="article-content-input"
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <Switch
                id="published"
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                data-testid="article-published-switch"
              />
              <Label htmlFor="published" className="cursor-pointer text-slate-700">
                {isEdit ? 'Published' : 'Publish immediately'}
              </Label>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-600 hover:bg-slate-100">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
                disabled={loading}
                data-testid="submit-article-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEdit ? 'Saving...' : 'Creating...'}
                  </>
                ) : isEdit ? 'Save Changes' : 'Create Article'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
